import { lazy, Suspense, useEffect, useState } from 'react'
import { api, haalSessie, opUitgelogd } from './lib/api'
import { navigeer, useRoute } from './lib/router'
import { meldFout, zet } from './lib/staat'
import Poort from './onderdelen/Poort'
import Shell from './onderdelen/Shell'
import { Laden, Leeg } from './onderdelen/ui'
import Start from './onderdelen/Start'

/* De schermen pas laden als iemand ze opent. */
const Verkenner = lazy(() => import('./onderdelen/verkenner/Verkenner'))
const Boeking = lazy(() => import('./onderdelen/Boeking'))
const Grootboek = lazy(() => import('./onderdelen/grootboek/Grootboek'))
const Vergelijken = lazy(() => import('./onderdelen/grootboek/Vergelijken'))
const Aansluiting = lazy(() => import('./onderdelen/grootboek/Aansluiting'))
const Relaties = lazy(() => import('./onderdelen/Relaties'))
const Relatie = lazy(() => import('./onderdelen/Relaties').then((m) => ({ default: m.Relatie })))
const RelatieWijzigen = lazy(() => import('./onderdelen/relatie/Wijzigen'))
const NieuweRelatie = lazy(() => import('./onderdelen/NieuweRelatie'))
const Live = lazy(() => import('./onderdelen/Live'))
const Stand = lazy(() => import('./onderdelen/Stand'))
const Logboek = lazy(() => import('./onderdelen/Logboek'))
/* Inzicht, verhuisd uit het dashboard: het managementoverzicht, de groep en
   de rapportage. Alle drie kijken naar dezelfde saldi uit het archief. */
const Inzicht = lazy(() => import('./onderdelen/inzicht/Overzicht'))
const Groep = lazy(() => import('./onderdelen/inzicht/Groep'))
const Rapportage = lazy(() => import('./onderdelen/inzicht/Rapportage'))

type Stand_ = 'kijken' | 'binnen' | 'buiten' | 'niets'

export default function App() {
  const [stand, setStand] = useState<Stand_>('kijken')

  /* Een fout uit de adresbalk (?fout=geen-toegang) wordt één keer getoond en
     dan weggehaald, zodat hij er na verversen niet nog steeds staat. */
  const [fout] = useState(() => new URLSearchParams(location.search).get('fout'))
  useEffect(() => {
    if (!fout) return
    const u = new URL(location.href)
    u.searchParams.delete('fout')
    history.replaceState(null, '', u.pathname + u.search)
  }, [fout])

  useEffect(() => {
    opUitgelogd(() => { zet({ ik: null }); setStand('buiten') })
    void (async () => {
      const s = await haalSessie()
      if (!s) { setStand('buiten'); return }
      zet({ ik: s.ik })
      try {
        const w = await api.ik()
        zet({
          management: w.management, divisies: w.divisies, catalogus: w.catalogus, velden: w.velden, paden: w.paden, mongo: w.mongo, magRelaties: w.magRelaties === true,
        })
        setStand(w.divisies.length ? 'binnen' : 'niets')
      } catch (e) {
        /* Een 403 hier: het recht is ingetrokken sinds de inlog. */
        meldFout(e)
        setStand('niets')
      }
    })()
  }, [])

  if (stand === 'kijken') return <div className="opstart"><Laden /></div>
  if (stand === 'buiten') return <Poort fout={fout} />
  if (stand === 'niets') {
    return (
      <div className="opstart">
        <Leeg titel="Je ziet hier (nog) geen administraties"
          tekst="Je bent ingelogd, maar er staat geen enkele bv van Exact voor je open. Welke bv's je mag zien stel je in het dashboard in (bij je profiel, onder administraties); vraag het iemand van management." />
      </div>
    )
  }

  return (
    <Shell>
      <Suspense fallback={<Laden />}>
        <Scherm />
      </Suspense>
    </Shell>
  )
}

function Scherm() {
  const { delen } = useRoute()
  const [eerste, tweede, derde, vierde] = delen

  switch (eerste) {
    case undefined: return <Start />
    case 'v': return <Verkenner soortSleutel={tweede ?? 'boekingen'} />
    case 'boeking': return tweede && derde ? <Boeking bv={tweede} entryId={derde} /> : <NietGevonden />
    case 'grootboek': return <Grootboek />
    case 'vergelijken': return <Vergelijken />
    case 'aansluiting': return <Aansluiting />
    case 'inzicht': return <Inzicht />
    case 'groep': return <Groep />
    case 'rapportage': return <Rapportage />
    case 'relaties': return tweede === 'nieuw' ? <NieuweRelatie /> : <Relaties />
    case 'relatie':
      if (tweede && derde && vierde === 'wijzigen') return <RelatieWijzigen bv={tweede} code={derde} />
      return tweede && derde ? <Relatie bv={tweede} code={derde} /> : <Relaties />
    case 'live': return <Live />
    case 'archief': return <Stand />
    case 'logboek': return <Logboek />
    default: return <NietGevonden />
  }
}

function NietGevonden() {
  return (
    <div className="pagina">
      <Leeg titel="Deze pagina bestaat niet" tekst="Misschien is de link oud, of verkeerd overgenomen.">
        <button type="button" className="knop k-gewoon" onClick={() => navigeer('/')}>Naar het overzicht</button>
      </Leeg>
    </div>
  )
}
